/**
 * Price groups (178, release 1.58) — a customer carries his price.
 *
 * A group is a RULE, not a price list: 'wholesale' = the product's wholesale
 * price where it has one, else its retail price; 'pct' = N % off the retail
 * price. The owner makes the groups and puts customers in them; the cashier
 * only picks the customer, and the cart prices itself (lib/cart-pricing.ts).
 *
 * THE SERVER DECIDES. create_sale (179) accepts a line at the group price of
 * the customer on the sale and computes it with private.group_unit_price —
 * `round(sale_price × (100 − pct) / 100, 2)` on numeric, half up. This file is
 * its twin and must agree to the tetri: the arithmetic is done in whole tetri
 * and basis points, integers only, so a float never decides a tetri. Both
 * sides are proven on the same twelve rows (price-groups.test.ts and
 * P-178-fixtures in migrations/proof/178_179_180_release_158.sql).
 */
import type { Customer, PriceGroup } from './mock-data'

export function groupUnitPrice(
  group: Pick<PriceGroup, 'rule' | 'pct'>,
  line: { unit_price: number; wholesale_price?: number | null },
): number {
  if (group.rule === 'wholesale') return line.wholesale_price ?? line.unit_price
  const tetri = Math.round(line.unit_price * 100)
  const factorBp = Math.round((100 - (group.pct ?? 0)) * 100)
  return Math.floor((tetri * factorBp + 5000) / 10000) / 100
}

/** The group that prices this customer: his group when it is switched on, else none. */
export function activeGroupOf(customer: Pick<Customer, 'price_group_id'> | null | undefined, groups: readonly PriceGroup[]): PriceGroup | null {
  const id = customer?.price_group_id
  if (!id) return null
  return groups.find(g => g.id === id && g.active) ?? null
}

/** „ხელოსანი · −10%" — the chip, the line tag and the receipt say the same. */
export function groupLabel(group: Pick<PriceGroup, 'name' | 'rule' | 'pct'>): string {
  return group.rule === 'pct' && group.pct != null ? `${group.name} · −${Number(group.pct)}%` : group.name
}

/** A row of price_groups (178) as the store keeps it. */
export function mapPriceGroup(r: any): PriceGroup {
  return {
    id: r.id,
    name: r.name,
    rule: r.rule === 'pct' ? 'pct' : 'wholesale',
    pct: r.pct == null ? null : Number(r.pct),
    active: r.active !== false,
    created_at: r.created_at,
  }
}

/**
 * A customer in a group whose group the till has not read yet (181, Codex's final review of
 * 1.58, I4): his sale waits — sold at retail, or queued offline at retail, it would quietly
 * break his price. A customer in no group never waits.
 */
export function groupUnknown(customer: Pick<Customer, 'price_group_id'> | null | undefined, groupsRead: boolean): boolean {
  return !!customer?.price_group_id && !groupsRead
}

/**
 * The cart offers its buyer chip in a shop with an active group — and while the groups are not
 * read yet, so a customer picked then reaches `groupUnknown` and his sale waits instead of going
 * at retail (Codex's re-check of 1.58, I4).
 */
export function groupsShown(groups: readonly Pick<PriceGroup, 'active'>[], groupsRead: boolean): boolean {
  return !groupsRead || groups.some((g) => g.active)
}

/**
 * One customer read again by himself (picked in the cart): his fresh row replaces the cached one,
 * a customer gone on the server leaves the copy, one not in it yet joins — the rest untouched.
 */
export function customerRefreshed<T extends { id: string }>(customers: readonly T[], id: string, fresh: T | null): T[] {
  if (!fresh) return customers.filter((c) => c.id !== id)
  return customers.some((c) => c.id === id) ? customers.map((c) => (c.id === id ? fresh : c)) : [...customers, fresh]
}
